import { Column, Label, Row } from '@ydistri/ds';
import React, { useCallback, useMemo } from 'react';
import { EntityIdentificationType } from '@ydistri/api-sdk';
import AdministrationItemSelect from './AdministrationItemSelect';
import { ReactSelectType, SelectionType } from './administrationItemsLib';

interface EntityIdentificationTypeComponentProps {
  type: 'Product' | 'Store';
  disabled?: boolean;
  value?: EntityIdentificationType;
  onChange: (selectedValue: EntityIdentificationType, id?: string) => void;
  id?: string;
  label?: string;
}

/**
 * Lets the user select how a product or warehouse are identified.
 * They can be identified either by Id or Code.
 * Used in Product list and Target list administration.
 * @param type Type of select makes the wording change
 * @param isDisabled whether the component is selectable or not
 * @param onChange callback when the user selects different value
 * @param value Current value
 * @constructor
 */
const EntityIdentificationTypeComponent: React.FC<EntityIdentificationTypeComponentProps> =
  React.memo(({ type, disabled, onChange, value, label, id, ...rest }) => {
    const changeHandler = useCallback(
      (newValue: SelectionType) => {
        if (
          newValue === EntityIdentificationType.Code ||
          newValue === EntityIdentificationType.CustomerId
        ) {
          onChange(newValue, id);
        }
      },
      [id, onChange],
    );

    const options = useMemo(() => {
      const selectOptions: ReactSelectType<EntityIdentificationType>[] = [
        { label: `${type} ID`, value: EntityIdentificationType.CustomerId },
        { label: `${type} Code`, value: EntityIdentificationType.Code },
      ];

      return selectOptions;
    }, [type]);

    return (
      <Row $alignItems="center" $gap="2rem" $flexWrap="nowrap">
        <Column $minWidth="13rem">
          <Label>{`${label ?? type} identifications:`}</Label>
        </Column>
        <Column $minWidth="20rem">
          <AdministrationItemSelect
            disabled={disabled}
            value={value}
            onChange={changeHandler}
            options={options}
            {...rest}
          />
        </Column>
      </Row>
    );
  });

EntityIdentificationTypeComponent.displayName = 'IdentificationTypeComponent';
export default EntityIdentificationTypeComponent;
